/**
 * <sticky-buy-bar>
 *
 * A bar pinned to the bottom of the viewport that carries the variant choice,
 * the price, and a real add to cart. It exists so a page that is mostly reading
 * never puts the buy decision more than a thumb away.
 *
 * The add itself is Horizon's. The bar wraps the theme's own
 * <product-form-component>, so submitting posts to /cart/add.js, updates the
 * bag count, and runs the added animation exactly as the product page does.
 * All this element owns is which variant is selected, when the bar is on
 * screen, and whether the cart opens afterwards.
 *
 * Variant switching is deliberately local. Horizon's variant picker refetches a
 * rendered section on every change, which is the right trade on a product page
 * where the gallery and description have to follow along. Here nothing follows
 * along except a price and a button, so a network round trip per tap would buy
 * nothing and add a way to fail.
 */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('input[name="id"]');
    this.button = this.querySelector('button[name="add"]');
    this.buttonLabel = this.querySelector('.add-to-cart-text__content');
    this.thumbEl = this.querySelector('[data-bar-thumb]');
    this.optionInputs = Array.from(this.querySelectorAll('input[data-variant-id]'));

    this.variants = this.#readVariants();
    this.soldOutText = this.dataset.soldOutText || 'Sold out';

    // Read the bare label from the attribute, never from the button, because
    // the rendered button already has the price appended to it.
    this.addText = this.dataset.addText || 'Add to cart';

    this.addEventListener('change', this.#onVariantChange);
    this.addEventListener('cart:update', this.#onCartUpdate);

    this.#watchReveal();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    this.#reserveSpace(false);
  }

  #readVariants() {
    const node = this.querySelector('[data-bar-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch {
      return [];
    }
  }

  /**
   * The bar is revealed either immediately or once the reader has committed to
   * the page. A sentinel is cheaper and steadier than a scroll handler, so the
   * scroll listener is only the fallback for the fixed-pixel case.
   */
  #watchReveal() {
    if (this.dataset.reveal === 'always') {
      this.#setVisible(true);
      return;
    }

    const after = parseInt(this.dataset.revealAfter, 10) || 100;
    this.revealAfter = (window.innerHeight * after) / 100;

    this.#onScroll();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #onScroll = () => {
    this.#setVisible(window.scrollY > this.revealAfter);
  };

  #setVisible(visible) {
    if (visible === this.visible) return;
    this.visible = visible;
    this.classList.toggle('sticky-buy-bar--visible', visible);
    this.#reserveSpace(visible);
  }

  /**
   * The bar is fixed, so at the bottom of the page it would sit on top of the
   * footer. Growing the document by the height of the bar keeps the last of the
   * footer reachable.
   *
   * This runs on reveal rather than on load because padding the page for a bar
   * that is usually not there would leave a gap under the footer. Adding it
   * mid-scroll shifts nothing: it only extends how far the page can scroll, and
   * every element above keeps its position.
   *
   * An earlier version retreated the bar when the footer came into view
   * instead. On a homepage barely two screens tall the footer arrives moments
   * after the bar does, so the bar flashed in and vanished for the rest of the
   * scroll, which read as broken.
   */
  #reserveSpace(reserve) {
    document.body.style.paddingBlockEnd = reserve ? `${this.offsetHeight}px` : '';
  }

  #onVariantChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.variantId) return;

    const variant = this.variants.find((v) => String(v.id) === input.dataset.variantId);
    if (!variant) return;

    if (this.variantInput) this.variantInput.value = variant.id;

    if (this.thumbEl && variant.media) {
      this.thumbEl.src = variant.media;
    }

    if (this.button) {
      this.button.disabled = !variant.available;
      if (this.buttonLabel) {
        // The price lives inside the button, so switching material rewrites the
        // label rather than a separate price node.
        this.buttonLabel.textContent = variant.available
          ? `${this.addText} · ${variant.price}`
          : this.soldOutText;
      }
    }
  };

  #onCartUpdate = (event) => {
    if (this.dataset.openCart !== 'true') return;
    if (event.detail?.data?.didError) return;

    // The drawer only exists when the merchant runs a drawer cart. On a page
    // cart there is nothing to open, and the bag count is the confirmation.
    const drawer = document.querySelector('cart-drawer-component');
    drawer?.open?.();
  };
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
